import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './Tickets.css';
import './Orders.css';

const DEFAULT_QUOTE_PROFIT_MARGIN = '15';
const DEFAULT_QUOTE_CATEGORY = 'Hardware med omvendt momspligt';
const CBC_ORDER_URL = 'https://cbcnet.dk/';

function getOrderNumber(order) {
  const value =
    order?.['zendesk-id'] ??
    order?.zendesk_id ??
    order?.zendeskId ??
    order?.id;
  if (value == null || value === '') return '—';
  return String(value);
}

function isHiddenOrderStatus(status) {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'closed' || normalized === 'solved';
}

function isVisibleOrder(order) {
  return !isHiddenOrderStatus(order?.status);
}

function OrderWorkflowProgress({ isAdmin, onCreateManual }) {
  if (!isAdmin) return null;

  const handleTicketClick = () => {
    window.dispatchEvent(new CustomEvent('navigateToTickets'));
  };

  const handleCbcClick = () => {
    window.open(CBC_ORDER_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="orders-card orders-workflow-card">
      <header className="orders-card-header">
        <h3>Order process</h3>
        <p>From ticket to customer acceptance</p>
      </header>
      <nav className="orders-workflow" aria-label="Order process">
        <ol className="orders-workflow-flow">
          <li className="orders-workflow-node">
            <button
              type="button"
              className="orders-workflow-dot clickable"
              onClick={handleTicketClick}
              title="Go to Tickets"
              aria-label="Create ticket"
            />
            <span className="orders-workflow-label">Create ticket</span>
          </li>

          <li className="orders-workflow-edge orders-workflow-edge-fork" aria-hidden="true">
            <svg viewBox="0 0 120 120" preserveAspectRatio="none" className="orders-workflow-edge-svg">
              <path d="M0 60 H36" />
              <path d="M36 60 C62 60, 62 30, 120 30" />
              <path d="M36 60 C62 60, 62 90, 120 90" />
            </svg>
          </li>

          <li className="orders-workflow-fork" aria-label="Order source">
            <div className="orders-workflow-fork-option">
              <button
                type="button"
                className="orders-workflow-dot clickable"
                onClick={handleCbcClick}
                title="Open CBCnet in a new tab"
                aria-label="Create CBC order"
              />
              <span className="orders-workflow-label">Create CBC order</span>
            </div>
            <div className="orders-workflow-fork-option">
              <button
                type="button"
                className="orders-workflow-dot clickable"
                onClick={() => onCreateManual?.()}
                title="Create a manual order from a Zendesk ticket"
                aria-label="Create Manual"
              />
              <span className="orders-workflow-label">Create Manual</span>
            </div>
          </li>

          <li className="orders-workflow-edge orders-workflow-edge-join" aria-hidden="true">
            <svg viewBox="0 0 120 120" preserveAspectRatio="none" className="orders-workflow-edge-svg">
              <path d="M0 30 C58 30, 58 60, 84 60" />
              <path d="M0 90 C58 90, 58 60, 84 60" />
              <path d="M84 60 H120" />
            </svg>
          </li>

          <li className="orders-workflow-node">
            <span className="orders-workflow-dot" aria-label="Create quote" />
            <span className="orders-workflow-label">Create quote</span>
          </li>

          <li className="orders-workflow-edge orders-workflow-edge-straight" aria-hidden="true">
            <svg viewBox="0 0 80 24" preserveAspectRatio="none" className="orders-workflow-edge-svg">
              <path d="M0 12 H80" />
            </svg>
          </li>

          <li className="orders-workflow-node">
            <span className="orders-workflow-dot" aria-label="Customer accept" />
            <span className="orders-workflow-label">Customer accept</span>
          </li>
        </ol>
      </nav>
    </section>
  );
}

function formatQuoteAmount(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/** Order JSON prices are ex-VAT (e-conomic uses ex-VAT amounts). */
function getLinePriceExVat(line) {
  if (!line) return null;
  if (line.priceExVat != null && line.priceExVat !== '') {
    const n = Number(line.priceExVat);
    return Number.isFinite(n) ? n : null;
  }
  if (line.priceIncVat != null && line.priceIncVat !== '') {
    const n = Number(line.priceIncVat);
    return Number.isFinite(n) ? n : null;
  }
  if (line.unitPrice !== '' && line.unitPrice != null && line.unitPrice !== undefined) {
    const n = Number(line.unitPrice);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Ex-VAT line amount: order-total price is not multiplied by qty. */
function lineExVatAmount(line) {
  const unitEx = getLinePriceExVat(line);
  if (unitEx == null || !Number.isFinite(unitEx)) return null;
  if (line.priceIsOrderTotal) {
    return unitEx;
  }
  const qty = Math.max(0, Number(line.quantity));
  const q = qty > 0 && Number.isFinite(qty) ? qty : 1;
  return unitEx * q;
}

/** Same rounding as server economicClient.roundMoney */
function roundQuoteMoney(n) {
  if (n == null || Number.isNaN(n)) return 0;
  return Math.round(Number(n) * 100) / 100;
}

/** Ex-VAT revenue when profit is margin % of that revenue: cost ÷ (1 − margin/100). */
function exVatSaleFromMarginOnSalePreview(costExVat, marginPct) {
  if (costExVat == null || !Number.isFinite(costExVat)) return null;
  const m = Number.isFinite(marginPct) ? Math.max(0, marginPct) : 0;
  if (m >= 100) return null;
  const d = 1 - m / 100;
  if (d <= 0) return null;
  return roundQuoteMoney(costExVat / d);
}

function getLineProfitMargin(line) {
  const raw = line?.profitMargin;
  if (raw === '' || raw == null || raw === undefined) return Number(DEFAULT_QUOTE_PROFIT_MARGIN);
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : Number(DEFAULT_QUOTE_PROFIT_MARGIN);
}

function profitMarginFromCatalogProduct(catalogProduct) {
  if (!catalogProduct) return null;
  const raw = catalogProduct.profitMargin;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n >= 100) return null;
  return String(n);
}

function normalizeProductNumber(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim().replace(',', '.');
  if (/^\d+(\.0+)?$/.test(s)) {
    return String(parseInt(s, 10));
  }
  const n = Number(s);
  if (Number.isFinite(n) && Number.isInteger(n)) {
    return String(n);
  }
  return String(value).trim();
}

function normalizeCategoryLabel(value) {
  return String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function categoriesMatch(a, b) {
  const na = normalizeCategoryLabel(a);
  const nb = normalizeCategoryLabel(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.toLowerCase() === nb.toLowerCase()) return true;
  return na.localeCompare(nb, 'da', { sensitivity: 'accent' }) === 0;
}

const Orders = () => {
  const { selectedTenant, authenticatedFetch, hasPrivilege, user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteOrder, setQuoteOrder] = useState(null);
  const [quoteTitle, setQuoteTitle] = useState('');
  const [cbcLines, setCbcLines] = useState([]);
  const [productDescriptions, setProductDescriptions] = useState([]);
  const [economicCatalog, setEconomicCatalog] = useState([]);
  const [lineCategories, setLineCategories] = useState({});
  const [quoteModalLoading, setQuoteModalLoading] = useState(false);
  const [quoteModalError, setQuoteModalError] = useState(null);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSubmitError, setQuoteSubmitError] = useState(null);
  const [quoteSubmitSuccess, setQuoteSubmitSuccess] = useState(null);
  const [quoteManualMode, setQuoteManualMode] = useState(false);
  const [manualTickets, setManualTickets] = useState([]);
  const [manualTicketsLoading, setManualTicketsLoading] = useState(false);
  const [selectedManualZendeskId, setSelectedManualZendeskId] = useState('');
  const [manualOrderCreating, setManualOrderCreating] = useState(false);

  const acceptOfferBusyRef = useRef(false);
  const [acceptOfferBusyId, setAcceptOfferBusyId] = useState(null);
  const ordersFetchGenRef = useRef(0);

  const fetchOrders = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      setOrders([]);
      return;
    }

    const tenantName = selectedTenant.name;
    const fetchGen = ordersFetchGenRef.current + 1;
    ordersFetchGenRef.current = fetchGen;

    try {
      setLoading(true);
      setError(null);
      setOrders([]);

      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/orders?tenant=${encodeURIComponent(tenantName)}`
      );

      const data = await response.json();

      if (fetchGen !== ordersFetchGenRef.current) return;

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch orders');
        setOrders([]);
      } else {
        const nextOrders = Array.isArray(data.orders) ? data.orders.filter(isVisibleOrder) : [];
        setOrders(nextOrders);
      }
    } catch (e) {
      if (fetchGen !== ordersFetchGenRef.current) return;
      console.error('Error fetching orders:', e);
      setError('Network error. Please try again.');
      setOrders([]);
    } finally {
      if (fetchGen === ordersFetchGenRef.current) {
        setLoading(false);
      }
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant) {
      fetchOrders();
    } else {
      ordersFetchGenRef.current += 1;
      setOrders([]);
      setLoading(false);
    }
  }, [selectedTenant, fetchOrders]);

  const categorySelectOptions = useMemo(() => {
    const set = new Set(
      (productDescriptions || [])
        .map((d) => normalizeCategoryLabel(d))
        .filter(Boolean)
    );
    set.add(DEFAULT_QUOTE_CATEGORY);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'da'));
  }, [productDescriptions]);

  const handleCloseQuoteModal = useCallback(() => {
    setQuoteModalOpen(false);
    setQuoteOrder(null);
    setQuoteTitle('');
    setCbcLines([]);
    setLineCategories({});
    setProductDescriptions([]);
    setEconomicCatalog([]);
    setQuoteModalLoading(false);
    setQuoteModalError(null);
    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);
    setQuoteSubmitting(false);
    setQuoteManualMode(false);
    setManualTickets([]);
    setManualTicketsLoading(false);
    setSelectedManualZendeskId('');
    setManualOrderCreating(false);
  }, []);

  const handleCreateQuote = (order) => {
    const zendeskCol = Object.keys(order).find((k) => k.toLowerCase() === 'zendesk-id');
    const zendeskId = zendeskCol ? order[zendeskCol] : '';
    const defaultTitle = `Quote — ${order.subject || zendeskId || `#${order.id}`}`;
    setQuoteManualMode(false);
    setSelectedManualZendeskId('');
    setManualTickets([]);
    setQuoteTitle(defaultTitle);
    setLineCategories({});
    setCbcLines([]);
    setProductDescriptions([]);
    setEconomicCatalog([]);
    setQuoteModalError(null);
    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);
    setQuoteSubmitting(false);
    setQuoteOrder(order);
    setQuoteModalOpen(true);
  };

  const handleCreateManual = useCallback(async () => {
    if (!selectedTenant?.name) return;

    setQuoteManualMode(true);
    setQuoteOrder(null);
    setQuoteTitle('');
    setLineCategories({});
    setCbcLines([]);
    setProductDescriptions([]);
    setEconomicCatalog([]);
    setQuoteModalError(null);
    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);
    setQuoteSubmitting(false);
    setSelectedManualZendeskId('');
    setQuoteModalOpen(true);
    setManualTicketsLoading(true);

    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/orders/manual-ticket-options?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setManualTickets([]);
        setQuoteModalError(data.error || 'Could not load Zendesk tickets.');
      } else {
        setManualTickets(Array.isArray(data.tickets) ? data.tickets : []);
      }
    } catch (err) {
      console.error('manual ticket options:', err);
      setManualTickets([]);
      setQuoteModalError('Network error while loading Zendesk tickets.');
    } finally {
      setManualTicketsLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  const startManualQuoteFromTicket = useCallback(async () => {
    if (!selectedTenant?.name || !selectedManualZendeskId || manualOrderCreating) return;

    const ticket =
      manualTickets.find((t) => String(t.zendeskId) === String(selectedManualZendeskId)) || null;
    setManualOrderCreating(true);
    setQuoteModalError(null);
    setQuoteSubmitError(null);

    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/orders/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant: selectedTenant.name,
          zendeskId: selectedManualZendeskId,
          subject: ticket?.subject || ''
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success || !data.order) {
        setQuoteModalError(data.error || 'Could not create manual order.');
        return;
      }

      const order = data.order;
      setQuoteOrder(order);
      setQuoteTitle(`Quote — ${order.subject || selectedManualZendeskId}`);
      fetchOrders();
    } catch (err) {
      console.error('create manual order:', err);
      setQuoteModalError('Network error while creating manual order.');
    } finally {
      setManualOrderCreating(false);
    }
  }, [
    selectedTenant,
    selectedManualZendeskId,
    manualOrderCreating,
    manualTickets,
    authenticatedFetch,
    fetchOrders
  ]);

  useEffect(() => {
    if (!quoteModalOpen || !quoteOrder || !selectedTenant) return undefined;

    let cancelled = false;

    async function loadQuoteModalData() {
      setQuoteModalLoading(true);
      setQuoteModalError(null);

      try {
        const tenantQ = encodeURIComponent(selectedTenant.name);
        const orderIdEnc = encodeURIComponent(String(quoteOrder.id));
        const descUrl = `${getApiBaseUrl()}/api/economic-products/descriptions`;
        const catalogUrl = `${getApiBaseUrl()}/api/economic-products/catalog`;
        const cbcUrl = `${getApiBaseUrl()}/api/orders/${orderIdEnc}/cbc?tenant=${tenantQ}`;

        const [descRes, catalogRes, cbcRes] = await Promise.all([
          authenticatedFetch(descUrl),
          authenticatedFetch(catalogUrl),
          authenticatedFetch(cbcUrl),
        ]);

        const descJson = await descRes.json().catch(() => ({}));
        const catalogJson = await catalogRes.json().catch(() => ({}));
        const cbcJson = await cbcRes.json().catch(() => ({}));

        if (cancelled) return;

        if (descRes.ok && descJson.success) {
          setProductDescriptions(Array.isArray(descJson.descriptions) ? descJson.descriptions : []);
        } else {
          setProductDescriptions([]);
          console.warn('economic-products/descriptions:', descJson.error || descRes.statusText);
        }

        const catalogProducts =
          catalogRes.ok && catalogJson.success && Array.isArray(catalogJson.products)
            ? catalogJson.products
            : [];
        setEconomicCatalog(catalogProducts);
        if (!catalogRes.ok || !catalogJson.success) {
          console.warn('economic-products/catalog:', catalogJson.error || catalogRes.statusText);
        }

        if (!cbcRes.ok || !cbcJson.success) {
          setCbcLines([]);
          const detail = cbcJson.detail;
          const detailStr =
            typeof detail === 'string' ? detail : detail != null ? JSON.stringify(detail) : '';
          setQuoteModalError(
            [cbcJson.error, detailStr].filter(Boolean).join(' — ') ||
              `Could not load order lines (${cbcRes.status}).`
          );
          return;
        }

        const rawLines = Array.isArray(cbcJson.lines) ? cbcJson.lines : [];
        const defaultCatalogProduct =
          catalogProducts.find((p) => categoriesMatch(p.categoryDescription, DEFAULT_QUOTE_CATEGORY)) ||
          null;
        const defaultLineMargin =
          profitMarginFromCatalogProduct(defaultCatalogProduct) || DEFAULT_QUOTE_PROFIT_MARGIN;
        let lines = rawLines.map((ln) => {
          const pFromApi =
            ln.priceExVat != null && ln.priceExVat !== '' && Number.isFinite(Number(ln.priceExVat))
              ? Number(ln.priceExVat)
              : ln.priceIncVat != null && ln.priceIncVat !== '' && Number.isFinite(Number(ln.priceIncVat))
                ? Number(ln.priceIncVat)
                : ln.unitPrice !== '' && ln.unitPrice != null && Number.isFinite(Number(ln.unitPrice))
                  ? Number(ln.unitPrice)
                  : null;
          return {
            ...ln,
            priceExVat: pFromApi,
            isManual: false,
            priceIsOrderTotal: !!ln.priceIsOrderTotal,
            profitMargin: defaultLineMargin
          };
        });

        if (lines.length === 0 && (quoteManualMode || quoteOrder.isManualOrder)) {
          const newKey = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
          lines = [
            {
              key: newKey,
              description: '',
              quantity: 1,
              unitPrice: '',
              currency: '',
              priceExVat: null,
              isManual: true,
              priceIsOrderTotal: false,
              profitMargin: defaultLineMargin,
              economicProductNumber: ''
            }
          ];
        }

        setCbcLines(lines);

        const initialCats = {};
        lines.forEach((ln) => {
          if (ln != null && ln.key != null) initialCats[String(ln.key)] = DEFAULT_QUOTE_CATEGORY;
        });
        setLineCategories(initialCats);
      } catch (e) {
        if (!cancelled) {
          console.error('Quote modal load:', e);
          setQuoteModalError('Network error while loading quote data.');
          setCbcLines([]);
        }
      } finally {
        if (!cancelled) setQuoteModalLoading(false);
      }
    }

    loadQuoteModalData();
    return () => {
      cancelled = true;
    };
  }, [quoteModalOpen, quoteOrder, selectedTenant, authenticatedFetch, quoteManualMode]);

  const catalogByCategory = useMemo(() => {
    const map = new Map();
    for (const product of economicCatalog) {
      const cat = normalizeCategoryLabel(product.categoryDescription);
      if (!cat) continue;
      if (!map.has(cat)) map.set(cat, product);
      const catKey = cat.toLocaleLowerCase('da-DK');
      if (!map.has(catKey)) map.set(catKey, product);
    }
    return map;
  }, [economicCatalog]);

  const resolveCatalogProductForCategory = useCallback(
    (categoryValue) => {
      const cat = normalizeCategoryLabel(categoryValue);
      if (!cat) return null;
      const fromMap =
        catalogByCategory.get(cat) || catalogByCategory.get(cat.toLocaleLowerCase('da-DK'));
      if (fromMap) return fromMap;
      return (
        economicCatalog.find((p) => categoriesMatch(p.categoryDescription, cat)) || null
      );
    },
    [economicCatalog, catalogByCategory]
  );

  const quoteTotals = useMemo(() => {
    let exVatSum = 0;
    let withMarginSum = 0;
    let marginTooHigh = false;
    for (const line of cbcLines) {
      const lineEx = lineExVatAmount(line);
      if (lineEx == null || !Number.isFinite(lineEx)) continue;
      exVatSum += lineEx;
      const m = getLineProfitMargin(line);
      if (m >= 100) marginTooHigh = true;
      const net = exVatSaleFromMarginOnSalePreview(lineEx, m);
      if (net == null) marginTooHigh = true;
      else withMarginSum += net;
    }
    return { exVatSum, withMarginSum, marginTooHigh };
  }, [cbcLines]);

  const handleAddQuoteLine = useCallback(() => {
    const newKey = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const defaultCatalogProduct = resolveCatalogProductForCategory(DEFAULT_QUOTE_CATEGORY);
    const defaultLineMargin =
      profitMarginFromCatalogProduct(defaultCatalogProduct) || DEFAULT_QUOTE_PROFIT_MARGIN;
    setCbcLines((prev) => [
      ...prev,
      {
        key: newKey,
        description: '',
        quantity: 1,
        unitPrice: '',
        currency: '',
        priceExVat: null,
        isManual: true,
        priceIsOrderTotal: false,
        profitMargin: defaultLineMargin,
        economicProductNumber: ''
      }
    ]);
    setLineCategories((prev) => ({ ...prev, [newKey]: DEFAULT_QUOTE_CATEGORY }));
  }, [resolveCatalogProductForCategory]);

  const handleRemoveQuoteLine = useCallback((key) => {
    setCbcLines((prev) => prev.filter((l) => String(l.key) !== String(key)));
    setLineCategories((prev) => {
      const next = { ...prev };
      delete next[String(key)];
      return next;
    });
  }, []);

  const handleQuoteLinePatch = useCallback((key, patch) => {
    setCbcLines((prev) =>
      prev.map((l) => (String(l.key) === String(key) ? { ...l, ...patch } : l))
    );
  }, []);

  const handlePriceExVatInput = useCallback((key, valueStr) => {
    if (valueStr === '' || valueStr == null) {
      handleQuoteLinePatch(key, { priceExVat: null, unitPrice: '' });
      return;
    }
    const n = Number(valueStr);
    if (Number.isFinite(n)) {
      handleQuoteLinePatch(key, { priceExVat: n, unitPrice: valueStr });
    }
  }, [handleQuoteLinePatch]);

  const applyCategoryCatalogAutofill = useCallback((lineKey, catalogProduct) => {
    if (!catalogProduct) return;
    setCbcLines((prev) =>
      prev.map((l) => {
        if (String(l.key) !== String(lineKey)) return l;
        const next = { ...l };
        const pn = normalizeProductNumber(catalogProduct.productNumber);
        if (pn) next.economicProductNumber = pn;

        if (!String(l.description || '').trim()) {
          next.description =
            catalogProduct.lineDescription || catalogProduct.categoryDescription || '';
        }

        const hasPrice = getLinePriceExVat(l) != null;
        if (!hasPrice && catalogProduct.priceExVat != null && Number.isFinite(catalogProduct.priceExVat)) {
          next.priceExVat = catalogProduct.priceExVat;
          next.unitPrice = String(catalogProduct.priceExVat);
        }

        const catalogMargin = profitMarginFromCatalogProduct(catalogProduct);
        next.profitMargin = catalogMargin || DEFAULT_QUOTE_PROFIT_MARGIN;

        return next;
      })
    );
  }, []);

  const handleLineCategoryChange = useCallback(
    (lineKey, value) => {
      const normalizedCategory = normalizeCategoryLabel(value);
      setLineCategories((prev) => ({ ...prev, [String(lineKey)]: normalizedCategory || value }));

      const catalogProduct = resolveCatalogProductForCategory(normalizedCategory || value);
      if (catalogProduct) {
        applyCategoryCatalogAutofill(lineKey, catalogProduct);
      }
    },
    [resolveCatalogProductForCategory, applyCategoryCatalogAutofill]
  );

  const handleLineMarginChange = useCallback(
    (lineKey, value) => {
      handleQuoteLinePatch(lineKey, { profitMargin: value });
    },
    [handleQuoteLinePatch]
  );

  const handleSubmitQuoteDraft = async (e) => {
    e.preventDefault();
    if (!selectedTenant || !quoteOrder || quoteSubmitting) return;

    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);

    const titleTrimmed = quoteTitle.trim();
    if (!titleTrimmed) {
      setQuoteSubmitError('Quote title is required.');
      return;
    }

    if (!cbcLines.length) {
      setQuoteSubmitError('No order lines to submit.');
      return;
    }

    for (let i = 0; i < cbcLines.length; i += 1) {
      const m = getLineProfitMargin(cbcLines[i]);
      if (m >= 100) {
        setQuoteSubmitError(`Line ${i + 1}: profit margin must be less than 100% (share of selling price).`);
        return;
      }
    }

    setQuoteSubmitting(true);
    try {
      const body = {
        tenant: selectedTenant.name,
        ticketId: quoteOrder.id,
        quoteTitle: titleTrimmed,
        lines: cbcLines.map((line) => ({
          key: line.key,
          description: line.description,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          currency: line.currency,
          priceExVat: getLinePriceExVat(line),
          categoryDescription: lineCategories[String(line.key)] || DEFAULT_QUOTE_CATEGORY,
          isManual: !!line.isManual,
          priceIsOrderTotal: !!line.priceIsOrderTotal,
          profitMargin: getLineProfitMargin(line),
          economicProductNumber: line.economicProductNumber || ''
        })),
      };

      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/orders/quote-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setQuoteSubmitError(data.error || (typeof data.detail === 'string' ? data.detail : '') || 'Submit failed.');
        return;
      }

      setQuoteSubmitSuccess(
        data.quoteNumber != null && data.quoteNumber !== ''
          ? `${data.message || 'Quote draft created.'} (e-conomic #${data.quoteNumber})`
          : data.message || 'Submitted.'
      );
      fetchOrders();
    } catch (err) {
      console.error(err);
      setQuoteSubmitError('Network error.');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleAcceptOffer = useCallback(
    async (order) => {
      if (!selectedTenant?.name || !order?.id || acceptOfferBusyRef.current) return;

      acceptOfferBusyRef.current = true;
      setAcceptOfferBusyId(order.id);

      try {
        const response = await authenticatedFetch(`${getApiBaseUrl()}/api/orders/accept-offer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant: selectedTenant.name,
            cbcOrderId: String(order.id),
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data.success) {
          const detail = data.detail;
          const nested =
            detail && typeof detail === 'object' && detail.message != null
              ? detail.message
              : detail;
          const msg =
            (typeof nested === 'string' ? nested : null) ||
            (typeof nested === 'object' ? JSON.stringify(nested) : null) ||
            (typeof data.error === 'string' ? data.error : null) ||
            'Could not update Zendesk.';
          window.alert(msg);
          return;
        }

        window.alert(
          'Thank you for your order!\n\nWe have received your acceptance and our team has been notified. We will proceed with ordering the equipment.'
        );
        fetchOrders();
      } catch (e) {
        console.error(e);
        window.alert('Network error.');
      } finally {
        acceptOfferBusyRef.current = false;
        setAcceptOfferBusyId(null);
      }
    },
    [selectedTenant, authenticatedFetch, fetchOrders]
  );

  const handleOpenQuotePdf = useCallback(
    async (e, order) => {
      e.preventDefault();
      if (!selectedTenant || !order) return;
      try {
        const params = new URLSearchParams({
          tenant: selectedTenant.name,
          cbcOrderId: String(order.id)
        });
        const response = await authenticatedFetch(
          `${getApiBaseUrl()}/api/orders/quote-pdf?${params.toString()}`
        );
        const ct = response.headers.get('content-type') || '';
        if (!response.ok) {
          let msg = 'Could not open quote PDF.';
          if (ct.includes('application/json')) {
            const data = await response.json().catch(() => ({}));
            msg =
              (typeof data.detail === 'string' ? data.detail : null) ||
              (typeof data.error === 'string' ? data.error : null) ||
              msg;
          }
          window.alert(msg);
          return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        console.error(err);
        window.alert('Network error.');
      }
    },
    [selectedTenant, authenticatedFetch]
  );

  const sortedOrders = useMemo(() => {
    const byZendesk = new Map();
    const withoutZendesk = [];

    for (const order of [...orders].filter(isVisibleOrder)) {
      const zd = String(
        order?.['zendesk-id'] ?? order?.zendesk_id ?? order?.zendeskId ?? ''
      ).trim();
      if (!zd) {
        withoutZendesk.push(order);
        continue;
      }

      const existing = byZendesk.get(zd);
      if (!existing) {
        byZendesk.set(zd, order);
        continue;
      }

      const existingIsManual = String(existing.id || '') === zd;
      const candidateIsManual = String(order.id || '') === zd;
      if (existingIsManual && !candidateIsManual) {
        byZendesk.set(zd, order);
      }
    }

    return [...byZendesk.values(), ...withoutZendesk].sort((a, b) => {
      const aValue = a.created_at ?? a.createdAt;
      const bValue = b.created_at ?? b.createdAt;
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      return new Date(bValue).getTime() - new Date(aValue).getTime();
    });
  }, [orders]);

  if (loading) {
    return (
      <div className="orders-page">
        <OrderWorkflowProgress isAdmin={isAdmin} onCreateManual={handleCreateManual} />
        <section className="orders-card orders-list-card">
          <div className="loading-container">
            <Icon name="tickets" size="large" className="loading-icon" />
            <p>Loading orders...</p>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-page">
        <OrderWorkflowProgress isAdmin={isAdmin} onCreateManual={handleCreateManual} />
        <section className="orders-card orders-list-card">
          <div className="error-container">
            <Icon name="tickets" size="large" className="error-icon" />
            <h3>Error Loading Orders</h3>
            <p>{error}</p>
            <button onClick={fetchOrders} className="retry-button">
              Retry
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <OrderWorkflowProgress isAdmin={isAdmin} onCreateManual={handleCreateManual} />
      <section className="orders-card orders-list-card">
      <div className="tickets-header">
        <div className="tickets-title-section">
          <h2>Orders</h2>
        </div>
        <div className="tickets-actions">
          <span className="tickets-count">
            {sortedOrders.length} order{sortedOrders.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="no-tickets-container">
          <Icon name="tickets" size="large" className="no-tickets-icon" />
          <h3>No Orders Found</h3>
          <p>No orders found.</p>
        </div>
      ) : (
        <div className="orders-table-wrap">
          <table className="orders-simple-table">
            <thead className="orders-table-sr-header">
              <tr>
                <th scope="col">Subject</th>
                <th scope="col">Order number</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((order, index) => {
                const subjectValue = order.subject;
                const subjectText =
                  subjectValue != null && subjectValue !== ''
                    ? String(subjectValue)
                    : '—';

                return (
                  <tr key={order.id || index} className="orders-simple-row">
                    <td className="orders-simple-cell orders-simple-subject">
                      {order.downloadlink ? (
                        <button
                          type="button"
                          className="order-subject-download-link"
                          onClick={(e) => handleOpenQuotePdf(e, order)}
                        >
                          {subjectText}
                        </button>
                      ) : (
                        subjectText
                      )}
                    </td>
                    <td className="orders-simple-cell orders-simple-order-number">
                      {getOrderNumber(order)}
                    </td>
                    <td className="orders-simple-cell orders-simple-actions">
                      <div className="order-actions">
                        {hasPrivilege('orders', 'create') && (
                          <button
                            className="order-action-button create-quote-button"
                            onClick={() => handleCreateQuote(order)}
                            title="Create Quote"
                          >
                            Create Quote
                          </button>
                        )}
                        {hasPrivilege('orders', 'accept') &&
                          (String(order.quote || '').trim().toLowerCase() === 'accepted' ? (
                            <span
                              className="orders-offer-accepted-label"
                              title="This order was accepted in the portal"
                            >
                              Offer accepted
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="order-action-button accept-offer-button"
                              onClick={() => handleAcceptOffer(order)}
                              disabled={
                                acceptOfferBusyId !== null && acceptOfferBusyId === order.id
                              }
                              title="Accept Offer"
                            >
                              {acceptOfferBusyId === order.id ? 'Submitting…' : 'Accept Offer'}
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </section>
      {quoteModalOpen && createPortal(
        <div
          className="ticket-form-overlay quote-draft-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseQuoteModal();
          }}
        >
          <div
            className="ticket-form-modal quote-draft-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-draft-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="ticket-form-header">
              <h3 id="quote-draft-title">
                {quoteManualMode && !quoteOrder ? 'Create Manual order' : 'Create quote'}
              </h3>
              <button
                type="button"
                className="ticket-form-close"
                onClick={handleCloseQuoteModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {quoteManualMode && !quoteOrder ? (
              <div className="ticket-form">
                <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.85 }}>
                  Select a Zendesk ticket. A manual order will be created with that ticket id so it
                  appears in the Orders list.
                </p>

                <div className="ticket-form-field">
                  <label htmlFor="manual-zendesk-ticket">Zendesk ticket</label>
                  <select
                    id="manual-zendesk-ticket"
                    value={selectedManualZendeskId}
                    onChange={(e) => setSelectedManualZendeskId(e.target.value)}
                    disabled={manualTicketsLoading || manualOrderCreating}
                  >
                    <option value="">
                      {manualTicketsLoading ? 'Loading tickets…' : '-- Select Zendesk ticket --'}
                    </option>
                    {manualTickets.map((ticket) => (
                      <option key={ticket.zendeskId} value={ticket.zendeskId}>
                        #{ticket.zendeskId}
                        {ticket.subject ? ` — ${ticket.subject}` : ''}
                        {ticket.status ? ` (${ticket.status})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {quoteModalError && <div className="ticket-form-error">{quoteModalError}</div>}

                <div className="ticket-form-actions">
                  <button type="button" className="secondary" onClick={handleCloseQuoteModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={startManualQuoteFromTicket}
                    disabled={
                      !selectedManualZendeskId || manualTicketsLoading || manualOrderCreating
                    }
                  >
                    {manualOrderCreating ? 'Creating…' : 'Continue to quote'}
                  </button>
                </div>
              </div>
            ) : (
            <form className="ticket-form" onSubmit={handleSubmitQuoteDraft}>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.85 }}>
                {quoteOrder?.subject || 'Order'}
                <span style={{ opacity: 0.6 }}>
                  {' '}
                  (order #{quoteOrder?.id})
                </span>
              </p>

              <div className="ticket-form-field">
                <label htmlFor="quote-draft-title-field">Quote title</label>
                <input
                  id="quote-draft-title-field"
                  type="text"
                  value={quoteTitle}
                  onChange={(e) => setQuoteTitle(e.target.value)}
                  autoComplete="off"
                  disabled={quoteModalLoading}
                />
              </div>

              {quoteModalLoading && <p style={{ margin: 0 }}>Loading order lines and categories…</p>}
              {quoteModalError && <div className="ticket-form-error">{quoteModalError}</div>}
              {!quoteModalLoading && !quoteModalError && cbcLines.length === 0 && (
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.55)' }}>
                  No lines were returned for this order. Use &quot;Add line&quot; to add rows manually.
                </p>
              )}
              {!quoteModalLoading && !productDescriptions.length && cbcLines.length > 0 && (
                <div className="ticket-form-error">
                  No categories found in economic-products (Description column empty or table missing).
                </div>
              )}

              {!quoteModalLoading && !quoteModalError && (
                <div className="ticket-form-field" style={{ gap: '0.75rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)' }}>
                      Order lines
                    </span>
                    <button
                      type="button"
                      className="quote-draft-add-line"
                      onClick={handleAddQuoteLine}
                      disabled={quoteModalLoading || quoteSubmitting}
                    >
                      + Add line
                    </button>
                  </div>

                  {cbcLines.length > 0 && (
                    <>
                      <div className="quote-draft-lines-scroll">
                        <div className="quote-draft-line-head">
                          <span>Line</span>
                          <span>Price (ex. VAT)</span>
                          <span>Margin %</span>
                          <span>Category</span>
                          <span aria-hidden="true" />
                        </div>
                        {cbcLines.map((line, lineIdx) => {
                          const priceEx = getLinePriceExVat(line);
                          const lineExVatLine = lineExVatAmount(line);
                          const lineMargin = line.profitMargin ?? DEFAULT_QUOTE_PROFIT_MARGIN;
                          return (
                            <div key={`quote-line-${String(line.key)}-${lineIdx}`} className="quote-draft-line-row">
                              <div>
                                <textarea
                                  className="quote-draft-desc-input quote-draft-desc-textarea"
                                  placeholder="Product description"
                                  rows={2}
                                  value={line.description || ''}
                                  onChange={(e) => handleQuoteLinePatch(line.key, { description: e.target.value })}
                                  disabled={quoteModalLoading || quoteSubmitting}
                                  aria-label={`Product description, line ${lineIdx + 1}`}
                                />
                                <div className="quote-draft-line-meta">
                                  {line.isManual ? (
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                      Qty
                                      <input
                                        type="number"
                                        min="0.001"
                                        step="any"
                                        style={{
                                          width: '4.25rem',
                                          padding: '0.2rem 0.35rem',
                                          borderRadius: 6,
                                          border: '1px solid rgba(255,255,255,0.2)',
                                          background: 'rgba(13, 17, 27, 0.95)',
                                          color: '#f5f7fa',
                                          fontSize: '0.8rem'
                                        }}
                                        value={line.quantity ?? 1}
                                        onChange={(e) => handleQuoteLinePatch(line.key, { quantity: e.target.value })}
                                        disabled={quoteModalLoading || quoteSubmitting}
                                      />
                                    </label>
                                  ) : (
                                    <span>Qty: {line.quantity ?? 1}</span>
                                  )}
                                  {line.currency ? (
                                    <span style={{ marginLeft: '0.5rem' }}>{String(line.currency)}</span>
                                  ) : null}
                                  {line.priceIsOrderTotal && lineExVatLine != null ? (
                                    <span style={{ marginLeft: '0.5rem' }}>· Order total (ex. VAT)</span>
                                  ) : null}
                                </div>
                              </div>
                              <input
                                type="number"
                                className="quote-draft-price-input"
                                min="0"
                                step="0.01"
                                placeholder="—"
                                value={priceEx != null ? String(priceEx) : ''}
                                onChange={(e) => handlePriceExVatInput(line.key, e.target.value)}
                                disabled={quoteModalLoading || quoteSubmitting}
                                aria-label={`Price excluding VAT, line ${lineIdx + 1}`}
                              />
                              <input
                                type="number"
                                className="quote-draft-margin-input"
                                min="0"
                                max="99.9"
                                step="0.1"
                                value={lineMargin}
                                onChange={(e) => handleLineMarginChange(line.key, e.target.value)}
                                disabled={quoteModalLoading || quoteSubmitting}
                                aria-label={`Profit margin percent, line ${lineIdx + 1}`}
                              />
                              <select
                                className="quote-draft-category-select"
                                aria-label={`Category for line ${lineIdx + 1}`}
                                value={lineCategories[String(line.key)] ?? DEFAULT_QUOTE_CATEGORY}
                                onChange={(e) => handleLineCategoryChange(line.key, e.target.value)}
                                disabled={quoteModalLoading || quoteSubmitting}
                              >
                                {categorySelectOptions.map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="quote-draft-remove-line"
                                onClick={() => handleRemoveQuoteLine(line.key)}
                                disabled={quoteModalLoading || quoteSubmitting}
                                aria-label={`Remove line ${lineIdx + 1}`}
                                title="Remove line"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      <div className="quote-draft-totals">
                        <div className="quote-draft-totals-row muted">
                          <span>Subtotal ex VAT</span>
                          <strong>{formatQuoteAmount(quoteTotals.exVatSum)}</strong>
                        </div>
                        <div className="quote-draft-totals-row">
                          <span>Total ex VAT (per-line margins applied)</span>
                          <strong>
                            {quoteTotals.marginTooHigh
                              ? '—'
                              : formatQuoteAmount(quoteTotals.withMarginSum)}
                          </strong>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {quoteSubmitError && <div className="ticket-form-error">{quoteSubmitError}</div>}
              {quoteSubmitSuccess && <div className="quote-draft-success">{quoteSubmitSuccess}</div>}

              <div className="ticket-form-actions">
                <button type="button" className="secondary" onClick={handleCloseQuoteModal} disabled={quoteSubmitting}>
                  Close
                </button>
                <button
                  type="submit"
                  disabled={
                    quoteModalLoading || quoteSubmitting || !!quoteModalError || cbcLines.length === 0
                  }
                >
                  Submit
                </button>
              </div>
            </form>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Orders;
